import { useEffect, useMemo, useRef, useState } from "react";
import { usePhysicsStore } from "../../../stores/physicsStore";

const CHART_MAX_POINTS = 200;

function buildRegistry(sensors) {
  const map = new Map();
  if (Array.isArray(sensors)) sensors.forEach((s) => map.set(s.name, { adr: s.adr, dim: s.dim }));
  return map;
}

export function useSensorDiagnostics(sensorName) {
  const storedMeta = usePhysicsStore((s) => s.sensorMeta);
  const isRunning = usePhysicsStore((s) => s.isRunning);
  const status = usePhysicsStore((s) => s.status);
  const packetRef = useRef({
    count: 0,
    sensordataLength: null,
    hasSensordata: false,
  });
  const frameRef = useRef(null);
  const [packet, setPacket] = useState({
    count: 0,
    sensordataLength: null,
    hasSensordata: false,
  });
  const [workerDiagnostics, setWorkerDiagnostics] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.__lastPhysicsSensorDiagnostics || null;
  });

  useEffect(() => {
    const flushPacket = () => {
      frameRef.current = null;
      setPacket(packetRef.current);
    };

    const onPoses = (e) => {
      const sensordata = e.detail?.sensordata;
      const hasSensordata = Array.isArray(sensordata);
      packetRef.current = {
        count: packetRef.current.count + 1,
        sensordataLength: hasSensordata ? sensordata.length : null,
        hasSensordata,
      };
      if (frameRef.current === null) {
        frameRef.current = requestAnimationFrame(flushPacket);
      }
    };
    window.addEventListener("physics-poses", onPoses);
    return () => {
      window.removeEventListener("physics-poses", onPoses);
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const onDiagnostics = (e) => {
      setWorkerDiagnostics(e.detail || null);
    };
    window.addEventListener("physics-sensor-diagnostics", onDiagnostics);
    return () => window.removeEventListener("physics-sensor-diagnostics", onDiagnostics);
  }, []);

  const meta = useMemo(() => {
    if (!Array.isArray(storedMeta) || !sensorName) return null;
    return storedMeta.find((entry) => entry?.name === sensorName) || null;
  }, [storedMeta, sensorName]);

  return {
    isRunning,
    status,
    meta,
    metaCount: Array.isArray(storedMeta) ? storedMeta.length : 0,
    workerDiagnostics,
    ...packet,
  };
}

export function useSensorChart(sensorName) {
  const storedMeta = usePhysicsStore((s) => s.sensorMeta);
  const registryRef = useRef(buildRegistry(storedMeta));
  const [channels, setChannels] = useState([]);
  const tRef = useRef(0);
  const pendingSamplesRef = useRef([]);
  const frameRef = useRef(null);

  useEffect(() => {
    const newRegistry = buildRegistry(storedMeta);
    registryRef.current = newRegistry;
    pendingSamplesRef.current = [];
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    setChannels([]);
    tRef.current = 0;
  }, [storedMeta]);

  useEffect(() => {
    const onMeta = (e) => {
      const sensors = e.detail?.sensors;
      if (!Array.isArray(sensors)) return;
      registryRef.current = buildRegistry(sensors);
      pendingSamplesRef.current = [];
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      setChannels([]);
      tRef.current = 0;
    };
    window.addEventListener("physics-sensor-meta", onMeta);
    return () => window.removeEventListener("physics-sensor-meta", onMeta);
  }, []);

  useEffect(() => {
    const flushSamples = () => {
      frameRef.current = null;
      const samples = pendingSamplesRef.current;
      if (!samples.length) return;
      pendingSamplesRef.current = [];

      setChannels((prev) => {
        let next = prev;
        for (const sample of samples) {
          const { dim, values, t } = sample;
          next = dim > 0 ? Array.from({ length: dim }, (_, ch) => next[ch] ?? []) : next;
          for (let ch = 0; ch < dim; ch++) {
            const pts = [...(next[ch] || []), { x: t, y: values[ch] }];
            next[ch] =
              pts.length > CHART_MAX_POINTS ? pts.slice(pts.length - CHART_MAX_POINTS) : pts;
          }
        }
        return next;
      });
    };

    const onPoses = (e) => {
      const sensordata = e.detail?.sensordata;
      if (!Array.isArray(sensordata) || !sensorName) return;
      const meta = registryRef.current.get(sensorName);
      if (!meta) return;
      const { adr, dim } = meta;
      if (adr + dim > sensordata.length) return;
      const t = ++tRef.current;
      pendingSamplesRef.current.push({
        t,
        dim,
        values: Array.from({ length: dim }, (_, ch) => sensordata[adr + ch]),
      });
      if (frameRef.current === null) {
        frameRef.current = requestAnimationFrame(flushSamples);
      }
    };
    window.addEventListener("physics-poses", onPoses);
    return () => {
      window.removeEventListener("physics-poses", onPoses);
      pendingSamplesRef.current = [];
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [sensorName]);

  return channels;
}
